/**
 * What a compile does to an SOP's scenario types: while it runs, the model's word on each type as it
 * lands; once saved, one line for the page and one mark per type.
 */

import type { TypeReconcileSummary } from '@/services/ai-function.service';
import type { TypeReconcileProgress } from '@/types/websocket.types';
import type { ScenarioType } from '../types';

export type TypeMark = 'new' | 'updated' | 'stale';

export type LiveVerdict = 'checking' | 'unchanged' | 'reworded' | 'no longer fits';

const LIVE_WORDS: Record<TypeReconcileProgress['checked'][number]['verdict'], LiveVerdict> = {
	unaffected: 'unchanged',
	updated: 'reworded',
	changed: 'no longer fits'
};

/** A preview: the rules turn "no longer fits" into stale or removed, and "reworded" into updated or replaced, on save. */
export const liveVerdict = (type: ScenarioType, progress: TypeReconcileProgress | null): LiveVerdict | null => {
	if (!progress) return null;
	const entry = progress.checked.find((checked) => checked.typeId === type.id);
	return entry ? LIVE_WORDS[entry.verdict] : 'checking';
};

/** "9 kept · 1 updated · 1 removed · 2 new"; a verdict no type got is left out. */
export const reconcileLine = (summary: TypeReconcileSummary): string =>
	[
		`${summary.kept.length} kept`,
		summary.updated.length > 0 && `${summary.updated.length} updated`,
		summary.stale.length > 0 && `${summary.stale.length} stale`,
		summary.removed.length > 0 && `${summary.removed.length} removed`,
		summary.added.length > 0 && `${summary.added.length} new`
	]
		.filter((part): part is string => Boolean(part))
		.join(' · ');

/** From the last compile alone: a recompile rewords the graph, so a type's own outcome no longer matching it says nothing. */
export const typeMark = (type: ScenarioType, summary: TypeReconcileSummary | null): TypeMark | null => {
	if (summary?.stale.includes(type.id)) return 'stale';
	if (summary?.added.includes(type.id)) return 'new';
	if (summary?.updated.includes(type.id)) return 'updated';
	return null;
};
